'use client';

import { cn } from '../../../../lib/utils';
import { ReactNode } from 'react';
import { ArrowDropDown } from '@crabs-ai/web-icons';
import TooltipTriggerIcon, {
  TooltipIconType,
} from '../../../primitives/feedback-and-status/tooltip/TooltipTriggerIcon';
import { TooltipProps } from '../../../primitives/feedback-and-status/tooltip/Tooltip';
import {
  getHeaderSizeStyles,
  getHeaderPaddingClass,
  type HeaderSize,
} from '../utils/header-styles';

export interface AccordionHeaderProps {
  className?: string;
  /** 제목 */
  title: string;
  /** Leading 아이콘 */
  leadingIcon?: ReactNode;
  /** Leading 아이콘 표시 여부 */
  isShowLeadingNode?: boolean;
  /** Tooltip 아이콘 표시 여부 */
  isShowTooltipIcon?: boolean;
  /** Tooltip 아이콘 타입 */
  tooltipIconType?: TooltipIconType;
  /** Tooltip 텍스트 */
  tooltipText?: string;
  /** 추가 Tooltip Props */
  tooltipProps?: Partial<Omit<TooltipProps, 'textNode' | 'children'>>;
  /** Label 표시 여부 */
  isShowLabel?: boolean;
  /** Label 노드 */
  labels?: ReactNode;
  /** Description 표시 여부 */
  isShowDesc?: boolean;
  /** Description 텍스트 */
  description?: string;
  /** Description 커스텀 클래스 */
  descriptionClassName?: string;
  /** 열림/닫힘 상태 (화살표 회전용) */
  isOpened?: boolean;
  /** 클릭 핸들러 */
  onClick?: () => void;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 반응형 여부 */
  responsive?: boolean;
  /** 사이즈 */
  size?: HeaderSize;
}

export default function AccordionHeader({
  className = '',
  title,
  leadingIcon,
  isShowLeadingNode = false,
  isShowTooltipIcon = false,
  tooltipIconType = 'help',
  tooltipText,
  tooltipProps,
  isShowLabel = false,
  labels,
  isShowDesc = false,
  description,
  descriptionClassName,
  isOpened = false,
  onClick,
  disabled = false,
  responsive = false,
  size = 'lg',
}: AccordionHeaderProps) {
  const currentSize = getHeaderSizeStyles(size);
  const paddingClass = getHeaderPaddingClass(responsive);

  const titleColorClassName = disabled
    ? 'text-sys-cont-neutral-disabled-default'
    : 'text-sys-cont-neutral-default';
  const iconColorClassName = disabled
    ? '[&_path]:fill-sys-cont-neutral-disabled-default'
    : '[&_path]:fill-sys-cont-neutral-default';

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'group-header accordion-header',
        'w-full rounded-xs bg-sys-bg-surface py-2xs',
        paddingClass,
        'flex flex-col gap-2xs',
        'transition-all',
        !disabled && 'hover:bg-sys-style-neutral-light',
        !disabled && 'active:bg-sys-style-neutral-default',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <div className="flex w-full items-center justify-between gap-2xs">
        <div className="group-header-left flex items-center gap-4xs">
          {isShowLeadingNode && leadingIcon && (
            <div
              className={cn(
                'group-header-leading-icon flex items-center [&_path]:fill-sys-cont-neutral-default',
                currentSize.icon,
              )}
            >
              {leadingIcon}
            </div>
          )}
          <h3
            className={cn(
              'group-header-title flex items-center',
              currentSize.title,
              titleColorClassName,
            )}
          >
            {title}
          </h3>
          {isShowTooltipIcon && tooltipText && (
            <TooltipTriggerIcon
              iconType={tooltipIconType}
              tooltipText={tooltipText}
              iconColorClassName={iconColorClassName}
              isNonInteractiveTrigger
              tooltipProps={tooltipProps}
            />
          )}
          {isShowLabel && labels && (
            <div className="group-header-labels flex items-center">
              {labels}
            </div>
          )}
        </div>
        <div className="group-header-trailing flex items-center">
          <ArrowDropDown
            className={cn(
              'size-5 transition-transform duration-200',
              isOpened && 'rotate-180',
              '[&_path]:fill-sys-cont-neutral-default',
            )}
          />
        </div>
      </div>
      {isShowDesc && description && (
        <p
          className={cn(
            'group-header-description line-clamp-2 text-left',
            currentSize.description,
            descriptionClassName,
            'text-sys-cont-neutral-light',
          )}
        >
          {description}
        </p>
      )}
    </button>
  );
}
